export type DurationUnit = 'm' | 'h' | 'd';

const formatToK = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 0,
});

export const count = (value?: number | null) =>
  value !== undefined && value !== null ? formatToK.format(value) : undefined;

const percentageNumberFormat = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

export const formatPercentage = (value?: number | null) =>
  value !== undefined && value !== null
    ? percentageNumberFormat.format(value)
    : undefined;

export function isDateValid(value: string | null) {
  return value ? !isNaN(new Date(value) as unknown as number) : false;
}

// takes a date string and returns a formatted date YYYY-MM-DD HH:mm:ss
export const formatDate = (value?: string | null) => {
  if (!value || !isDateValid(value)) {
    return undefined;
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatDurationString = (timeString?: string | null) => {
  if (!timeString) {
    return undefined;
  }

  // The string needs to be in the format of "1d", "2h", "3m", "4s" etc.
  // That is the string will contain a number and a letter as a unit.
  // For example, "10m" should be { duration: 10, unit: "m" }
  // If the string is not in the correct format, return undefined
  if (!timeString.match(/^\d+[dhms]$/)) {
    return undefined;
  }

  const value = timeString.slice(0, -1);
  const unit = timeString.slice(-1) as DurationUnit;

  return { value, unit };
};

export const formatNumberWithSign = (
  number: number,
  precision: number = 2,
): string => {
  const formattedNumber = number.toFixed(precision);
  if (number > 0) {
    return `+${formattedNumber}`;
  } else {
    return formattedNumber;
  }
};

export function formatCompactNumber(num: number): string {
  if (num < 1000) {
    return num.toString();
  }

  const units = ['k', 'M', 'B', 'T'];
  const unitIndex = Math.floor(Math.log10(num) / 3) - 1;
  const unit = units[Math.min(unitIndex, units.length - 1)];
  const value = num / Math.pow(1000, unitIndex + 1);

  // Format to 1 decimal place if needed, remove trailing zeros
  const formatted = value.toFixed(1);
  return formatted.endsWith('.0')
    ? formatted.slice(0, -2) + unit
    : formatted + unit;
}
